'use client';

import type { ImgProps } from 'next/dist/shared/lib/get-img-props';
import Head from 'next/head';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';
import { type SourceHTMLAttributes, useMemo, useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from './types';
import getSrcUrl from './utils/getSrcUrl';

interface LocalizedSourceHTMLAttributes
  extends SourceHTMLAttributes<HTMLSourceElement> {
  src: MessageKey;
}

export interface ArtDirectionImageProps
  extends Omit<ImageProps, 'src' | 'localizedSrc'> {
  readonly localizedSrcSet?: LocalizedSourceHTMLAttributes[] | undefined;
  readonly srcSet?: SourceHTMLAttributes<HTMLSourceElement>[] | undefined;
}

const ArtDirectionImage = ({
  alt = '',
  className,
  format = 'jpg',
  localizedAlt,
  localizedSrcSet,
  priority,
  sizes,
  srcSet,
  ...props
}: ArtDirectionImageProps) => {
  const t = useTranslations();
  const imagePropsRef = useRef<Omit<ImgProps, 'srcSet'> | null>(null);
  const sources = useMemo((): (SourceHTMLAttributes<HTMLSourceElement> & {
    key: string;
  })[] => {
    const _srcSet =
      (localizedSrcSet
        ? localizedSrcSet.map(({ src, ...source }) => ({
            ...source,
            src: t(src),
          }))
        : srcSet) ?? [];

    return _srcSet.map(({ media, src, ...attrs }, i) => {
      if (!src) {
        return {
          key: i.toString(),
          media,
          srcSet: '',
          ...attrs,
        };
      }

      const {
        props: { srcSet: imagePropsSrcSet, ...rest },
      } = getImageProps({
        alt,
        src: getSrcUrl(src, format),
        ...props,
      });

      imagePropsRef.current = rest;

      return {
        key: src + media,
        media,
        srcSet: imagePropsSrcSet,
        ...attrs,
      };
    });
  }, [localizedSrcSet, t, srcSet, format, alt, props]);

  return (
    <>
      {priority && (
        <Head>
          {sources.map(({ srcSet: _srcSet }) => (
            <link
              rel="preload"
              as="image"
              key={_srcSet}
              href={imagePropsRef.current?.src}
              imageSrcSet={_srcSet}
              imageSizes={imagePropsRef.current?.sizes}
            />
          ))}
        </Head>
      )}
      <picture data-cy="art-direction-image">
        {sources.map(({ key, media, srcSet: _srcSet, ...attrs }) => (
          <source
            data-cy="art-direction-image-source"
            key={key}
            media={media}
            sizes={sizes}
            srcSet={_srcSet}
            {...attrs}
          />
        ))}
        <img
          {...imagePropsRef.current}
          alt={localizedAlt ? t(localizedAlt) : alt}
          className={className}
          data-cy="art-direction-image-img"
          sizes={undefined}
        />
      </picture>
    </>
  );
};

export default ArtDirectionImage;
